import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import { Box, VBox } from "../../components-v2/Box";
import { DecisionItem, DetailsPanel, getDecisionDetailsPanels } from "../common/commonDomain";
import {
  BoardMinutesCustomPanel,
  getCustomBoardMeetingPanels,
  SignersMode,
} from "./boardMeetingMinutesDomain";
import {
  CollapseList,
  DefaultLanguageSettingsTip,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentSigningStatusLine,
  LocalDateTimePicker,
  ReopenDocumentButton,
} from "../common/commonComponents";
import { BoardMeetingMinutesForm, VotingModel } from "./BoardMeetingMinutesForm";
import { Alert } from "../../components-v2/Alert";
import { Checkbox, MCheckboxGroup } from "../../components-v2/Checkbox";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { AttachmentsPanel } from "../../components-v2/AttachmentsPanel";
import { AgendaActions } from "../common/commonAgendaComponents";
import { MTextInput, TextInput } from "../../components-v2/TextInput";
import { BoardMeetingForm } from "../noticeBoardMeeting/noticeBoardMeetingDomain";
import { when } from "../../utils/functions";
import { MDateInput } from "../../components-v2/DateInput";
import { Caption } from "../../components-v2/Caption";
import { isProductionEnvironment } from "../../utils/environments";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { Icon } from "../../components-v2/Icon";
import { isPending } from "../../common/common";
import { DecisionType } from "../../common/decisions";
import { useLanguage, useTranslation } from "../../stores/RootStoreContext";
import { partition } from "lodash";
import {
  AddNameAndEmailForm,
  RadioFieldQuestion,
  SelectField,
} from "../../common/commonComponents";
import { relativeTime } from "../../i18n/dateFormat";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";

const ExtraParticipants: FC<{ form: BoardMeetingMinutesForm }> = observer(({ form }) => {
  const { t } = useTranslation();

  return (
    <>
      <Box>
        <div>{t("documents.boardMeeting.whoAttendedExtraParticipants")}</div>
      </Box>
      {form.extraParticipants.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            tooltip={t("remove")}
            onClick={() => form.removeExtraParticipant(p)}
            disabled={form.isLocked}
            compact
          />
        </Box>
      ))}

      <Box>
        {!form.isLocked && form.extraParticipantFormModel ? (
          <AddNameAndEmailForm
            form={form.extraParticipantFormModel}
            submit={form.submitExtraParticipant}
            cancel={form.cancelAddExtraParticipant}
          />
        ) : (
          <LinkButton
            label={t("documents.boardMeeting.whoAttendedExtraParticipants.addExternal")}
            icon={"plus"}
            onClick={form.addExtraParticipant}
            disabled={form.isLocked}
          />
        )}
      </Box>
    </>
  );
});

const VotedAgainst = observer(
  ({
    voting,
    form,
    decision,
  }: {
    voting?: VotingModel;
    form: BoardMeetingMinutesForm;
    decision: DecisionItem;
  }) => {
    const { t } = useTranslation();
    if (!voting) {
      return null;
    }

    const unapprovedOutcome = form.unapprovedVotingOutcomeForDecision(
      decision.decisionId,
      form.votingListUsingReview(decision),
    );

    return (
      <>
        <div>{t("documents.voting.votingHelperTitle")}</div>
        {!voting.hasParticipatingMembers ? (
          <Alert
            type={"warning"}
            message={t("documents.boardMeeting.noParticipatingBoardMembersSelected")}
          />
        ) : (
          <>
            <MCheckboxGroup
              model={voting.votedAgainst}
              onChange={(_, member) => form.toggleVotedAgainst(member, voting.decisionId)}
              disabled={form.isLocked}
            />
            {unapprovedOutcome && (
              <Box>
                <Alert
                  type="warning"
                  message={
                    t("dbot.boardmeeting.votingOutcome.notApproved") + ": " + t(unapprovedOutcome)
                  }
                />
              </Box>
            )}
          </>
        )}
      </>
    );
  },
);

const VotedByReview = observer(
  ({
    voting,
    form,
    decision,
  }: {
    voting?: VotingModel;
    form: BoardMeetingMinutesForm;
    decision: DecisionItem;
  }) => {
    if (!voting) {
      return null;
    }
    const decisionVoters = partition(
      form.votingListUsingReview(decision),
      (v) => v.vote === undefined,
    );
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <VBox>
        {decisionVoters[0].map((v) => (
          <Box key={v.companyroleId} alignItems={"center"}>
            <Icon name={"clock"} />
            {t("not-voted", { name: v.name })}
          </Box>
        ))}
        {decisionVoters[1]
          .sort((a) => (a.vote === "for" ? 1 : -1))
          .map((v) => (
            <Box key={v.companyroleId} alignItems={"center"}>
              <Icon name={v.vote === "for" ? "thumbs-up" : "thumbs-down"} />
              {t(v.vote === "for" ? "voted-for" : "voted-against", { name: v.name })}{" "}
              <div className={"subtle"}>{v.votedAt ? t(relativeTime(v.votedAt, locale)) : ""}</div>
            </Box>
          ))}
      </VBox>
    );
  },
);

const Voting = observer(function Voting({
  form,
  decision,
}: {
  form: BoardMeetingMinutesForm;
  decision: DecisionItem;
}) {
  const { t } = useTranslation();
  const voting = form.votesByDecision.get(decision.decisionId);
  const isDecisionMatter = form.isDecisionMatter(decision.decisionId);

  if (!isDecisionMatter) {
    return null;
  }

  return (
    <>
      <strong>{t("dbot.decision")}</strong>
      <MRadioButtonGroup
        model={form.votingModeModelForDecision(decision.decisionId)}
        disabled={form.isLocked}
      />
      {form.isVotedAgainst(decision) && (
        <VotedAgainst voting={voting} form={form} decision={decision} />
      )}
      {form.isVotedUsingReview(decision) && (
        <VotedByReview voting={voting} form={form} decision={decision} />
      )}
    </>
  );
});

export const BoardMeetingAgendaItemDetails = observer(function BoardMeetingAgendaItemDetails({
  form,
  decisionId,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  form: BoardMeetingMinutesForm;
  decisionId: string;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
  if (!decision) {
    return null;
  }

  const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
  return (
    <VBox gap={"md"}>
      {renderDecisionForm(decisionId)}
      {decision.type !== DecisionType.freetext && editor && (
        <LocalizedTextEditor model={editor.model} />
      )}
      {renderDecisionAttachment(decisionId)}
      <Voting form={form} decision={decision} />
    </VBox>
  );
});

const DecisionsForIncompetent: FC<{
  form: BoardMeetingMinutesForm;
  memberId: number;
}> = observer(({ form, memberId }) => {
  const { t } = useTranslation();

  return (
    <>
      {form.currentDecisions.map((decision) => {
        return (
          <Box key={decision.decisionId} marginLeft={"lg"}>
            <Checkbox
              label={t(decision.title)}
              onChange={(event) =>
                form.toggleDecisionForIncompetentMember(
                  memberId,
                  decision.decisionId,
                  event.target.checked,
                )
              }
              checked={form.isIncompetentForDecision(memberId, decision.decisionId)}
              disabled={form.isLocked}
            />
          </Box>
        );
      })}
    </>
  );
});

export const BoardMeetingSignatories = observer(function BoardMeetingSigners({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox>
        <RadioFieldQuestion
          question={{ key: "documents.boardMeeting.whoWillSign" }}
          fieldName={"physicalMeetingSignersMode"}
          field={form.physicalMeetingSignersMode}
          onChange={form.setPhysicalMeetingSignersMode}
          isLocked={form.isLocked || !form.isSelectionOfSignersEnabled}
        />
        {!form.isSelectionOfSignersEnabled && (
          <Alert type={"info"} message={t("dbot.boardmeeting.all-must-sign-when-circulation")} />
        )}
        {form.physicalMeetingSignersMode.value === SignersMode.SELECTED && (
          <VBox gap={"md"}>
            <Box marginLeft={"lg"}>
              <MCheckboxGroup model={form.selectedSigningBoardMembers} disabled={form.isLocked} />
            </Box>
            <MDateInput
              label={t("documents.boardMeeting.whenIsDeadlineForComments")}
              model={form.deadlineForNotes}
            />
            <Caption>{t("documents.boardMeeting.deadlineCommentsDate-helperText")}</Caption>
          </VBox>
        )}
      </VBox>
    </VBox>
  );
});

export const BoardMeetingChair = observer(function BoardMeetingChair({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox>
        <SelectField
          field={form.meetingChair}
          name={"meetingChair"}
          onChange={form.setMeetingChair}
          isLocked={form.isLocked || !form.canSelectMeetingChair}
        />
      </VBox>
      {form.cannotSelectMeetingChairMessage && (
        <Alert type={"info"} message={t(form.cannotSelectMeetingChairMessage)} />
      )}
    </VBox>
  );
});

export const BoardMeetingParticipants = observer(function BoardMeetingParticipants({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      {form.boardMeetingForm.value !== BoardMeetingForm.CIRCULATION && (
        <VBox>
          <div>{t("documents.boardMeeting.whoAttended")}</div>
          <Checkbox
            label={t("all")}
            onChange={(event) => form.toggleAllBoardMembers(event.target.checked)}
            checked={form.selectedBoardMembers.selected.length === form.boardMembers.length}
            disabled={form.isLocked}
          />
          <VBox>
            <CollapseList
              closeLabel={t("collapse")}
              openLabel={t("dbot.showAll")}
              initiallyExpanded={
                form.selectedBoardMembers.selected.length !== form.boardMembers.length
              }
            >
              <VBox>
                <MCheckboxGroup
                  model={form.selectedBoardMembers}
                  onChange={(event, option) => form.toggleSelectedBoardMember(option)}
                  disabled={form.isLocked}
                />
              </VBox>
            </CollapseList>
          </VBox>
        </VBox>
      )}
      {form.boardMeetingForm.value !== BoardMeetingForm.CIRCULATION &&
        form.hasDeputyBoardMembers && (
          <VBox>
            <div>{t("documents.boardMeeting.whoAttendedDeputy")}</div>
            <Checkbox
              label={t("all")}
              onChange={(event) => form.toggleAllDeputyBoardMembers(event.target.checked)}
              checked={
                form.selectedDeputyMembers.selected.length === form.deputyBoardMembers.length
              }
              disabled={form.isLocked}
            />
            <VBox>
              <CollapseList
                closeLabel={t("collapse")}
                openLabel={t("dbot.showAll")}
                initiallyExpanded={
                  form.selectedDeputyMembers.selected.length !== 0 &&
                  form.selectedDeputyMembers.selected.length !== form.deputyBoardMembers.length
                }
              >
                <VBox>
                  <MCheckboxGroup
                    model={form.selectedDeputyMembers}
                    onChange={(event, option) => form.toggleSelectedDeputyBoardMember(option)}
                    disabled={form.isLocked}
                  />
                </VBox>
              </CollapseList>
            </VBox>
          </VBox>
        )}
      {form.boardMeetingForm.value !== BoardMeetingForm.CIRCULATION && form.hasObservers && (
        <VBox>
          <div>{t("documents.boardMeeting.whoAttendedObserver")}</div>
          <Checkbox
            label={t("all")}
            onChange={(event) => form.selectedObservers.toggleAll(event.target.checked)}
            checked={form.selectedObservers.isAllSelected}
            disabled={form.isLocked}
          />
          <VBox>
            <CollapseList
              closeLabel={t("collapse")}
              openLabel={t("dbot.showAll")}
              initiallyExpanded={
                !form.selectedObservers.isAllSelected && !form.selectedObservers.isNoneSelected
              }
            >
              <VBox>
                <MCheckboxGroup model={form.selectedObservers} disabled={form.isLocked} />
              </VBox>
            </CollapseList>
          </VBox>
        </VBox>
      )}
      {form.boardMeetingForm.value !== BoardMeetingForm.CIRCULATION && (
        <ExtraParticipants form={form} />
      )}
      <VBox>
        <Box>
          <div>{t("documents.boardMeeting.incompetentBoardMembers")}</div>
        </Box>
        <CollapseList closeLabel={t("collapse")} openLabel={t("dbot.showAll")}>
          <VBox>
            {form.presentBoardMembers.map((member) => (
              <VBox key={`${member.name}_member`}>
                <Checkbox
                  label={member.name}
                  onChange={(event) =>
                    form.toggleIncompetentBoardMember(member.companyroleId, event.target.checked)
                  }
                  checked={form.isIncompetent(member.companyroleId)}
                  disabled={form.isLocked}
                />
                {form.isIncompetent(member.companyroleId) && (
                  <VBox>
                    <DecisionsForIncompetent form={form} memberId={member.companyroleId} />
                  </VBox>
                )}
              </VBox>
            ))}
          </VBox>
        </CollapseList>
      </VBox>
      <LocalizedTextEditor model={form.additionalParticipantDetailsTextModel} />
    </VBox>
  );
});

export const BoardMeetingDetails = observer(function BoardMeetingDetails({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <DefaultLanguageSettingsTip
        isLocked={form.isLocked}
        hasSavedDefaultDocumentLanguage={form.context.hasSavedDefaultDocumentLanguage}
        orgNo={form.context.orgNo}
        dispatch={form.dispatch}
        dismissedDefaultDocumentLanguageTip={
          form.context.userPrefs.dismissedDefaultDocumentLanguageTip ?? false
        }
      />
      <MTextInput
        name={"boardMeetingNumber"}
        label={t("documents.noticeBoardMeeting.boardMeetingNumberHelperText")}
        prefix={"#"}
        model={form.boardMeetingNumber}
        readOnly={form.isLocked}
      />
      <VBox>
        <RadioFieldQuestion
          question={{ key: "documents.meetingform" }}
          field={form.boardMeetingForm}
          fieldName={"boardMeetingForm"}
          row
          isLocked={form.isLocked}
        />
      </VBox>
      {form.boardMeetingForm.value !== BoardMeetingForm.CIRCULATION && (
        <VBox>
          <Box>
            <div>{t("documents.boardMeeting.dateBoardMeetingQuestion")}</div>
          </Box>
          <LocalDateTimePicker
            dateLabel={{ key: "date" }}
            dateField={form.meetingStartDate}
            timeLabel={{ key: "documents.boardMeeting.dateBoardMeetingStartTimeLabel" }}
            timeField={form.meetingStartTime}
            endTimeLabel={{
              key: "documents.boardMeeting.dateBoardMeetingEndTimeLabel",
            }}
            endTimeField={form.meetingEndTime}
            isLocked={form.isLocked}
          />
        </VBox>
      )}
      {form.showAddress && (
        <RadioFieldQuestion
          field={form.meetingPlace}
          fieldName={"meetingPlace"}
          question={{ key: "documents.noticeBoardMeeting.noticeToBoardMeetingPlace" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showInlineAddress && (
        <TextInput
          label={t("documents.meetingForm.address")}
          value={form.inlineAddress.inputValue}
          onChange={form.inlineAddress.setInputValue}
          onBlur={form.inlineAddress.commit}
          readOnly={form.isLocked}
        />
      )}
      <LocalizedTextEditor model={form.additionalDetailsTextModel} />
    </VBox>
  );
});

export const BoardMeetingMinutesDetailsPanel = observer(function BoardMeetingMinutesDetailsPanel({
  panel,
  form,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  panel: DetailsPanel;
  form: BoardMeetingMinutesForm;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  switch (panel.type) {
    case "decision":
      return (
        <BoardMeetingAgendaItemDetails
          form={form}
          decisionId={panel.id}
          renderDecisionForm={renderDecisionForm}
          renderDecisionAttachment={renderDecisionAttachment}
        />
      );
    case "custom":
      switch (panel.id) {
        case BoardMinutesCustomPanel.meetingDetails:
          return <BoardMeetingDetails form={form} />;
        case BoardMinutesCustomPanel.participants:
          return <BoardMeetingParticipants form={form} />;
        case BoardMinutesCustomPanel.meetingChair:
          return <BoardMeetingChair form={form} />;
        case BoardMinutesCustomPanel.signatories:
          return <BoardMeetingSignatories form={form} />;
      }
  }
  return null;
});

export const BoardMeetingMinutesView2 = observer(({ form }: { form: BoardMeetingMinutesForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox gap="xs">
        <DocumentSigningStatusLine context={form.context} dispatch={form.dispatch} />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getCustomBoardMeetingPanels(form)}
        />
      </VBox>
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={form.isLocked}
        panels={getDecisionDetailsPanels(form)}
      />
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={form.uploadAttachments}
      />
      <AttachmentsPanel
        title={false}
        uploadedAttachments={Array.from(form.attachments.values())}
        onOpen={form.openAttachment}
        onDelete={form.isLocked ? undefined : form.deleteAttachmentById}
      />
      {when(form.sendForReviewAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      {when(form.openSendForSigningDialogAction, (action) => (
        <Button
          variant={"primary"}
          label={t(action.label)}
          loading={isPending(form.context.documentSigners.get(form.documentId))}
          onClick={(e) => {
            if (e.shiftKey && !isProductionEnvironment()) {
              form.dispatch({
                type: "dbot/autosign-document",
                document: form.context.currentDocuments.find(
                  (d) => d.documentId === form.documentId,
                )!,
                label: { translated: "Autosign document" },
              });
            } else {
              form.dispatch(action);
            }
          }}
        />
      ))}
      {when(form.downloadSignedDocumentAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      <ReopenDocumentButton context={form.context} dispatch={form.dispatch} />
    </VBox>
  );
});
